import type { EyemedLoginState } from '../../shared/eyemedLogin'

/**
 * What a clinic's EyeMed row on the Integrations list says about its saved
 * login (openspec change eyemed-mfa-email-codes, frontend/eyemed-mfa-setup).
 * Pure. The state comes from `listEyemedLoginStates`; `loading` is that fetch
 * still in flight and `failed` one that did not answer — neither ever reads as
 * Configured.
 */
export type EyemedRowInput = EyemedLoginState | 'loading' | 'failed'

export interface EyemedRowStatus {
  /** Shown in place of "Configured". */
  label: string
  tone: 'green' | 'amber' | 'red' | 'grey'
  /** Whether EyeMed counts toward the clinic's total of configured
   *  integrations: only when nothing is left for the clinic to do. */
  counts: boolean
}

export function eyemedRowStatus(input: EyemedRowInput): EyemedRowStatus {
  switch (input) {
    case 'not_set_up':
      return { label: 'Finish setup', tone: 'amber', counts: false }
    case 'set_up':
      return { label: 'Configured', tone: 'green', counts: true }
    case 'paused_admin':
      return { label: 'Checks paused', tone: 'red', counts: false }
    case 'paused_vitalrev':
      return { label: 'VitalRev is fixing', tone: 'grey', counts: true }
    case 'loading':
      return { label: 'Checking…', tone: 'grey', counts: false }
    case 'failed':
      return { label: 'Status unavailable', tone: 'grey', counts: false }
  }
}

/**
 * A clinic's row input from the page's fetch: still loading, failed, or the
 * clinic's state. A clinic the fetch did not list reads not set up, as the
 * backend reads a login with no doc.
 */
export function eyemedRowInput(
  states: ReadonlyMap<string, EyemedLoginState> | 'failed' | null,
  clinicId: string,
): EyemedRowInput {
  if (states === null) return 'loading'
  if (states === 'failed') return 'failed'
  return states.get(clinicId) ?? 'not_set_up'
}
